import { useEffect, useRef, useState } from "react";
import { COMMAND_CHIPS, TYPEWRITER_PHRASES } from "../../mock";

// En dev: el proxy de package.json forwardea /api/* a localhost:8000
// (la URL absoluta de fallback es para si el proxy no está activo).
// En build: REACT_APP_API_URL viene de .env.production (o .env.local en dev).
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const FALLBACK = {
  intent: "Tu sistema IA",
  fases: [
    {
      num: "01",
      nivel: "Fundamentos",
      title: "Mapea los 3 procesos que más tiempo te roban",
      desc: "Identifica qué tareas repites cada semana.",
      action: "Ver video intro",
    },
    {
      num: "02",
      nivel: "Configuración",
      title: "Configura tu primer flujo en una hora",
      desc: "Conecta tu correo con IA usando n8n.",
      action: "Descargar plantilla",
    },
    {
      num: "03",
      nivel: "Ejecución",
      title: "Prueba el sistema esta semana",
      desc: "Corre el flujo 7 días y mide el tiempo ahorrado.",
      action: "Acceder al kit",
    },
    {
      num: "04",
      nivel: "Escalamiento",
      title: "Conviértelo en sistema completo",
      desc: "Agrega agentes, WhatsApp y reportes automáticos.",
      action: "Agendar con Paco",
    },
  ],
};

const LOADING_STEPS = [
  "Analizando tu objetivo...",
  "Consultando base de conocimiento...",
  "Construyendo tu ruta personalizada...",
  "Preparando recursos y herramientas...",
];

const FASE_STYLES = [
  { bg: "rgba(17,17,16,0.08)", color: "var(--ink)" },
  { bg: "rgba(17,17,16,0.92)", color: "var(--white)" },
  { bg: "rgba(139,69,19,0.18)", color: "var(--ink)" },
  { bg: "rgba(17,17,16,0.08)", color: "var(--ink)" },
];

export default function CommandCenter() {
  const [mode, setMode] = useState("idle"); // 'idle' | 'loading' | 'result'
  const [query, setQuery] = useState("");
  const [activeChip, setActiveChip] = useState(null);
  const [loadingStep, setLoadingStep] = useState(0);
  const [result, setResult] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const inputRef = useRef(null);

  // Typewriter placeholder
  const [phIndex, setPhIndex] = useState(0);
  const [phText, setPhText] = useState("");
  const [phPhase, setPhPhase] = useState("typing"); // typing | pausing | deleting
  const isPaused = mode !== "idle" || query.length > 0;

  useEffect(() => {
    if (isPaused) return;
    const target = TYPEWRITER_PHRASES[phIndex];
    let t;
    if (phPhase === "typing") {
      if (phText.length < target.length) {
        t = setTimeout(
          () => setPhText(target.slice(0, phText.length + 1)),
          55 + Math.random() * 30
        );
      } else {
        setPhPhase("pausing");
        t = setTimeout(() => setPhPhase("deleting"), 1700);
      }
    } else if (phPhase === "deleting") {
      if (phText.length > 0) {
        t = setTimeout(() => setPhText(target.slice(0, phText.length - 1)), 28);
      } else {
        setPhIndex((i) => (i + 1) % TYPEWRITER_PHRASES.length);
        setPhPhase("typing");
      }
    }
    return () => clearTimeout(t);
  }, [phText, phPhase, phIndex, isPaused]);

  const runQuery = async (q) => {
    if (!q || !q.trim()) return;
    setMode("loading");
    setLoadingStep(0);
    setResult(null);

    const interval = setInterval(() => {
      setLoadingStep((s) => Math.min(s + 1, LOADING_STEPS.length - 1));
    }, 380);

    try {
      const res = await fetch(`${API_URL}/api/command-center`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q.trim() }),
      });

      if (res.status === 429) {
        clearInterval(interval);
        setLimitReached(true);
        setMode("idle");
        return;
      }

      if (!res.ok) throw new Error("api-error");
      const parsed = await res.json();

      if (!parsed?.intent || !Array.isArray(parsed?.fases) || parsed.fases.length !== 4) {
        throw new Error("bad-shape");
      }

      clearInterval(interval);
      setLoadingStep(LOADING_STEPS.length - 1);
      setTimeout(() => {
        setResult(parsed);
        setMode("result");
      }, 320);
    } catch (e) {
      clearInterval(interval);
      setLoadingStep(LOADING_STEPS.length - 1);
      setTimeout(() => {
        setResult(FALLBACK);
        setMode("result");
      }, 280);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    runQuery(query);
  };

  const onChip = (chip) => {
    setQuery(chip.query);
    setActiveChip(chip.label);
    inputRef.current?.focus();
  };

  const reset = () => {
    setMode("idle");
    setQuery("");
    setActiveChip(null);
    setResult(null);
    setLoadingStep(0);
  };

  return (
    <section
      id="ai-command"
      className="reveal"
      style={{
        position: "relative",
        zIndex: 2,
        background: "var(--surface-light)",
        padding: "100px 28px",
      }}
    >
      <div style={{ maxWidth: 920, margin: "0 auto" }}>
        <DividerLabel>AI Command Center</DividerLabel>

        <h2
          className="font-display"
          style={{
            fontSize: "clamp(2rem, 4.6vw, 3.6rem)",
            fontWeight: 500,
            letterSpacing: "-0.02em",
            lineHeight: 1.04,
            margin: "0 0 16px",
            textAlign: "center",
            color: "var(--ink)",
          }}
        >
          Dime qué quieres construir con{" "}
          <em style={{ fontStyle: "italic", fontWeight: 400 }}>IA.</em>
        </h2>

        <p
          style={{
            textAlign: "center",
            color: "var(--ink-dim)",
            fontSize: 15,
            lineHeight: 1.6,
            maxWidth: 540,
            margin: "0 auto 44px",
          }}
        >
          Escribe tu objetivo y te genero una ruta personalizada paso a paso.
        </p>

        {limitReached && (
          <div
            className="terminal-box"
            style={{ textAlign: "center", padding: "40px 28px" }}
          >
            <h3
              className="font-display"
              style={{
                fontSize: "1.4rem",
                fontWeight: 500,
                letterSpacing: "-0.015em",
                margin: "0 0 10px",
              }}
            >
              Has usado tus 3 consultas gratuitas.
            </h3>
            <p
              style={{
                color: "var(--ink-dim)",
                fontSize: 14,
                lineHeight: 1.55,
                margin: "0 auto 24px",
                maxWidth: 460,
              }}
            >
              Has usado tus 3 consultas gratuitas del día. Vuelve mañana o
              agenda una llamada para una ruta personalizada completa.
            </p>
            <button
              type="button"
              className="btn-primary"
              onClick={() =>
                document
                  .getElementById("contacto")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              Agenda una llamada con Paco →
            </button>
          </div>
        )}

        {!limitReached && mode === "idle" && (
          <div className="terminal-box">
            <TerminalBar />
            <form
              onSubmit={onSubmit}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "16px 18px",
                borderBottom: "1px solid var(--line)",
              }}
            >
              <span
                style={{
                  fontFamily: "ui-monospace, SFMono-Regular, monospace",
                  fontSize: 16,
                  color: "var(--ink-dim)",
                  paddingRight: 6,
                }}
              >
                ›
              </span>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (activeChip) setActiveChip(null);
                }}
                placeholder={query.length === 0 ? phText : ""}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: 15,
                  color: "var(--ink)",
                  padding: "6px 0",
                }}
                aria-label="Tu objetivo con IA"
              />
              <button type="submit" className="btn-primary">
                Ejecutar →
              </button>
            </form>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                padding: "14px 16px 16px",
              }}
            >
              {COMMAND_CHIPS.map((chip) => {
                const active = activeChip === chip.label;
                return (
                  <button
                    type="button"
                    key={chip.label}
                    onClick={() => onChip(chip)}
                    style={{
                      fontFamily: '"DM Sans", sans-serif',
                      fontSize: 12,
                      padding: "7px 14px",
                      borderRadius: 100,
                      border: active
                        ? "1px solid var(--ink)"
                        : "1px solid var(--line)",
                      background: active ? "var(--ink)" : "transparent",
                      color: active ? "var(--white)" : "var(--ink)",
                      cursor: "pointer",
                      transition: "all 0.18s",
                    }}
                    onMouseEnter={(e) => {
                      if (active) return;
                      e.currentTarget.style.background = "var(--ink)";
                      e.currentTarget.style.color = "var(--white)";
                    }}
                    onMouseLeave={(e) => {
                      if (active) return;
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = "var(--ink)";
                    }}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {!limitReached && mode === "loading" && (
          <div className="terminal-box">
            <TerminalBar />
            <div style={{ padding: "26px 22px 22px" }}>
              <div
                style={{
                  fontSize: 13,
                  color: "var(--ink)",
                  fontWeight: 500,
                  marginBottom: 18,
                }}
              >
                {LOADING_STEPS[loadingStep]}
              </div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {LOADING_STEPS.map((step, idx) => {
                  const done = idx < loadingStep;
                  const active = idx === loadingStep;
                  return (
                    <li
                      key={step}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 0",
                        fontSize: 12,
                        color: done
                          ? "var(--ink)"
                          : active
                          ? "var(--ink)"
                          : "var(--ink-mute)",
                      }}
                    >
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 14,
                          fontWeight: 600,
                        }}
                      >
                        {done ? "✓" : active ? "›" : "·"}
                      </span>
                      {step}
                    </li>
                  );
                })}
              </ul>
              <div
                style={{
                  marginTop: 22,
                  height: 1,
                  width: "100%",
                  background: "var(--line)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    background: "var(--ink)",
                    width: `${
                      ((loadingStep + 1) / LOADING_STEPS.length) * 100
                    }%`,
                    transition: "width 0.32s ease",
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {!limitReached && mode === "result" && result && (
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                marginBottom: 22,
                flexWrap: "wrap",
              }}
            >
              <h3
                className="font-display"
                style={{
                  fontSize: "clamp(1.4rem, 3vw, 2rem)",
                  fontWeight: 500,
                  letterSpacing: "-0.02em",
                  margin: 0,
                  lineHeight: 1.1,
                }}
              >
                Tu sistema IA:{" "}
                <em style={{ fontStyle: "italic", fontWeight: 400 }}>
                  {result.intent}
                </em>
              </h3>
              <span className="eyebrow">Ruta generada</span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 14,
                marginBottom: 28,
              }}
            >
              {result.fases.map((f, idx) => {
                const s = FASE_STYLES[idx % FASE_STYLES.length];
                return (
                  <article
                    key={f.num + idx}
                    style={{
                      background: s.bg,
                      color: s.color,
                      borderRadius: 16,
                      padding: "20px 22px 22px",
                      border: "1px solid var(--line)",
                    }}
                  >
                    <div
                      style={{
                        fontFamily: "ui-monospace, monospace",
                        fontSize: 11,
                        letterSpacing: "0.18em",
                        opacity: 0.7,
                        marginBottom: 6,
                      }}
                    >
                      FASE {f.num}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "0.16em",
                        opacity: 0.65,
                        marginBottom: 14,
                      }}
                    >
                      {f.nivel}
                    </div>
                    <h4
                      className="font-display"
                      style={{
                        fontSize: "1.15rem",
                        fontWeight: 500,
                        lineHeight: 1.18,
                        letterSpacing: "-0.01em",
                        margin: "0 0 10px",
                      }}
                    >
                      {f.title}
                    </h4>
                    <p
                      style={{
                        fontSize: 13,
                        lineHeight: 1.55,
                        opacity: 0.8,
                        margin: "0 0 16px",
                      }}
                    >
                      {f.desc}
                    </p>
                    <button
                      type="button"
                      onClick={() => (window.location.hash = "contacto")}
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        padding: "7px 14px",
                        borderRadius: 100,
                        border: `1px solid ${
                          s.color === "var(--white)"
                            ? "rgba(255,255,255,0.4)"
                            : "var(--line-strong)"
                        }`,
                        background: "transparent",
                        color: "inherit",
                        cursor: "pointer",
                      }}
                    >
                      {f.action} →
                    </button>
                  </article>
                );
              })}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 10,
              }}
            >
              <a href="#contacto" className="btn-primary" style={{ justifyContent: "center" }}>
                Implementarlo con Paco →
              </a>
              <button
                type="button"
                className="btn-ghost"
                style={{ justifyContent: "center" }}
                onClick={() =>
                  alert("✦ Próximamente: te enviaremos esta ruta por correo.")
                }
              >
                Recibir esta ruta
              </button>
              <button
                type="button"
                onClick={reset}
                style={{
                  background: "transparent",
                  color: "var(--ink-dim)",
                  fontSize: 13,
                  fontWeight: 500,
                  padding: "10px 22px",
                  cursor: "pointer",
                }}
              >
                ↺ Nueva búsqueda
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .terminal-box {
          border-radius: 16px;
          background: var(--white);
          border: 1px solid var(--line);
          box-shadow: 0 24px 60px rgba(17,17,16,0.06);
          overflow: hidden;
        }
      `}</style>
    </section>
  );
}

function TerminalBar() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "10px 14px",
        borderBottom: "1px solid var(--line)",
        background: "rgba(17,17,16,0.03)",
      }}
    >
      <div style={{ display: "flex", gap: 6 }}>
        <Dot color="#ff5f57" />
        <Dot color="#febc2e" />
        <Dot color="#28c840" />
      </div>
      <span
        style={{
          fontFamily: "ui-monospace, SFMono-Regular, monospace",
          fontSize: 11,
          color: "var(--ink-dim)",
        }}
      >
        soypacoia — command center
      </span>
    </div>
  );
}

function Dot({ color }) {
  return (
    <span
      style={{
        width: 10,
        height: 10,
        borderRadius: "50%",
        background: color,
        display: "inline-block",
      }}
    />
  );
}

function DividerLabel({ children }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 18,
        marginBottom: 28,
        fontSize: 10,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: "var(--ink-dim)",
        fontWeight: 500,
      }}
    >
      <span
        style={{
          flex: 1,
          maxWidth: 80,
          height: 1,
          background: "var(--line)",
        }}
      />
      <span>{children}</span>
      <span
        style={{
          flex: 1,
          maxWidth: 80,
          height: 1,
          background: "var(--line)",
        }}
      />
    </div>
  );
}
